import React from "react";
import { Plus, Package, X } from "lucide-react";
import CustomDropdown from "./CustomDropdown";

export default function QuoteItemsTable({
  items,
  newItem,
  products,
  formatCurrency,
  handleProductSelect,
  updateItem,
  deleteItem,
  handleNewItemProductSelect,
  setNewItem,
  addItem,
}) {
  const resizeDescription = (element) => {
    if (!element) return;
    element.style.height = "28px";
    element.style.height = `${Math.min(element.scrollHeight, 96)}px`;
    element.style.overflowY = element.scrollHeight > 96 ? "auto" : "hidden";
  };

  // Helper function to get product details
  const getProductDetails = (productId) => {
    return products.find((p) => p._id === productId);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[#E9E5F1] bg-white">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="bg-[#FAF9FC] border-b border-[#E9E5F1]">
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[35%]">Item Details</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[15%]">Qty</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[20%]">Rate</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[20%]">Amount</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[10%]">Action</th>
          </tr>
        </thead>
        <tbody>
          {/* Existing Items */}
          {items.map((item, index) => {
            const productDetails = getProductDetails(item.productId);
            return (
              <tr
                key={`item-${index}-${item.productId || 'new'}`}
                className="border-b border-[#E6E6E6] hover:bg-[#F9F9F9] transition-colors"
              >
                {/* Item Details Column */}
                <td className="px-2 py-1.5" style={{ position: 'relative', zIndex: 100 - index }}>
                  <div className="space-y-1">
                    {/* Product Name with Icon */}
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {item.isPackage ? (
                          <Package className="h-5 w-5 text-purple-600 flex-shrink-0" title="Package" />
                        ) : (
                          <div className="h-5 w-5 text-[#AEADAF] flex-shrink-0">
                            <svg fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <CustomDropdown
                          products={products}
                          selected={item.productId}
                          disabled={item.isPackage}
                          formatCurrency={formatCurrency}
                          handleNewItemProductSelect={(productId) =>
                            handleProductSelect(index, productId)
                          }
                        />
                        {productDetails?.sku && (
                          <p className="mt-0.5 truncate text-[10px] leading-3 text-gray-500">
                            SKU: <span className="font-medium text-gray-700">{productDetails.sku}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="ml-8">
                      <label className="sr-only">
                        Description
                      </label>
                      <textarea
                        rows={1}
                        ref={resizeDescription}
                        value={item.description}
                        onChange={(e) =>
                          updateItem(index, "description", e.target.value)
                        }
                        onInput={(event) => resizeDescription(event.currentTarget)}
                        placeholder="Write an item description"
                        className="min-h-7 w-full resize-none rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs leading-4 text-gray-700 placeholder:text-gray-400 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-100"
                      />
                    </div>
                  </div>
                </td>

                {/* Qty */}
                <td className="px-1.5 py-1.5 align-middle">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      updateItem(index, "quantity", parseInt(e.target.value) || 0)
                    }
                    className="w-full text-center bg-[#F7F7F8] border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </td>

                {/* Rate */}
                <td className="px-1.5 py-1.5 align-middle">
                  <input
                    type="number"
                    value={item.rate}
                    onChange={(e) =>
                      updateItem(index, "rate", parseFloat(e.target.value) || 0)
                    }
                    className="w-full text-right bg-[#F7F7F8] border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </td>

                {/* Amount */}
                <td className="px-1.5 py-1.5 text-right align-middle">
                  <div className="font-semibold text-[#18181B]">
                    {formatCurrency(item.amount)}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-1.5 py-1.5 text-center align-middle">
                  <button
                    type="button"
                    onClick={() => deleteItem(index)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-100"
                    title="Remove item"
                    aria-label="Remove item"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </button>
                </td>
              </tr>
            );
          })}

          {/* Empty State */}
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-full border border-purple-100 bg-purple-50 text-purple-500">
                    <Package className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-semibold text-[#252035]">No items added yet</p>
                  <p className="text-xs text-[#8E8C8F]">Add products or services below to build this quote.</p>
                </div>
              </td>
            </tr>
          )}

          {/* New Item Input Row */}
          <tr className="border-t border-[#E9E5F1] bg-[#FBFAFD]">
            {/* Item Details Column */}
            <td className="relative px-2 py-1.5">
              <div className="space-y-1">
                {/* Product Selector */}
                <div className="flex items-start gap-2">
                  <div className="mt-1">
                    <div className="h-5 w-5 text-[#AEADAF] flex-shrink-0">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <CustomDropdown
                      formatCurrency={formatCurrency}
                      products={products}
                      selected={newItem.productId}
                      handleNewItemProductSelect={handleNewItemProductSelect}
                    />
                    {newItem.productId && (() => {
                      const productDetails = getProductDetails(newItem.productId);
                      return productDetails?.sku ? (
                        <p className="mt-0.5 truncate text-[10px] leading-3 text-gray-500">
                          SKU: <span className="font-medium text-gray-700">{productDetails.sku}</span>
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Description */}
                <div className="ml-8">
                  <label className="sr-only">
                    Description
                  </label>
                  <textarea
                    rows={1}
                    ref={resizeDescription}
                    placeholder="Write an item description"
                    value={newItem.description}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        description: e.target.value,
                      })
                    }
                    onInput={(event) => resizeDescription(event.currentTarget)}
                    className="min-h-7 w-full resize-none rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs leading-4 text-gray-700 placeholder:text-gray-400 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-100"
                  />
                </div>
              </div>
            </td>

            {/* Qty */}
            <td className="px-1.5 py-1.5 align-middle">
              <input
                type="number"
                value={newItem.quantity}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    quantity: parseInt(e.target.value) || 1,
                  })
                }
                className="w-full text-center bg-white border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </td>

            {/* Rate */}
            <td className="px-1.5 py-1.5 align-middle">
              <input
                type="number"
                step="0.01"
                value={newItem.rate}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    rate: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full text-right bg-white border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </td>

            {/* Amount */}
            <td className="px-1.5 py-1.5 text-right align-middle">
              <div className="font-semibold text-[#18181B]">
                {formatCurrency(newItem.quantity * newItem.rate)}
              </div>
            </td>

            {/* Add Button */}
            <td className="px-1.5 py-1.5 text-center align-middle">
              <button
                onClick={addItem}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-purple-600 text-white shadow-sm transition-colors hover:bg-purple-700"
                title="Add this item"
              >
                <Plus className="h-4 w-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
